var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getItemAbsoluteBox } from 'app/core/markers/bounds';
import MarkerRenderers from 'app/image/rasterize/markers';
import { renderPinMarker } from 'app/image/rasterize/markers/pin';
import { renderSpotsWithComposition } from 'app/image/rasterize/markers/spots';
import { renderTextMarker } from 'app/image/rasterize/markers/text';
import { renderZoomMaker } from 'app/image/rasterize/markers/zoom';
import { createLinearGradient } from 'app/utils/geom';
import { getEdgeTransparencyInsets } from './trimTransparency';
export function drawBitmap(canvasProvider, renderJob, image, images, blurredImage, watermark) {
    var _a, _b, _c, _d;
    return __awaiter(this, void 0, void 0, function* () {
        const { output, markup, opts } = renderJob;
        const { bounds, style } = output;
        const { width: imageWidth, height: imageHeight } = renderJob.image;
        // image size modifiers
        const padding = Math.max(0, (_a = style === null || style === void 0 ? void 0 : style.padding) !== null && _a !== void 0 ? _a : 0);
        const cornerRadius = Math.max(0, (_b = style === null || style === void 0 ? void 0 : style.corner) !== null && _b !== void 0 ? _b : 0);
        const shadow = Math.max(0, (_c = style === null || style === void 0 ? void 0 : style.shadow) !== null && _c !== void 0 ? _c : 0);
        const background = style === null || style === void 0 ? void 0 : style.background;
        // zoom & spots markers are the trouble makers
        const hasZooms = markup.marks.some(marker => marker.alias === 'Zoom');
        const hasSpots = markup.spots.length > 0;
        // // ignore cropping if there are any zooms
        // const isCropped =
        //     artBox.x > 0 ||
        //     artBox.y > 0 ||
        //     artBox.width < imageWidth ||
        //     artBox.height < imageHeight;
        // if there are zooms its possible to have a zoom source on edge
        // of the crop area, this causes an invalid render as source area
        // is restricted to canvas size.
        // in this case just ignore cropping and render on image size.
        // crop is then applied at the end
        // const applyCropAfterRender = isCropped && hasZooms;
        const renderArea = bounds;
        // applyCropAfterRender
        //     ? { x: 0, y: 0, width: imageWidth, height: imageHeight }
        //     : artBox;
        // setup background buffer if there are spots|zooms
        const bufferIsRequired = hasSpots || hasZooms;
        // slide render size =
        const renderWidth = bounds.width + 2 * padding;
        const renderHeight = bounds.height + 2 * padding;
        // render size
        const { x: artX, y: artY, width: artWidth, height: artHeight } = renderArea;
        // view to original image scale - only should be used if we are going to render x2 versions
        const viewToImageScale = 1;
        // MathUtils.getFitScale(imageWidth, imageHeight, outputWidth, outputHeight);
        // create secondary buffer if needed
        let bufferCtx;
        if (bufferIsRequired) {
            const buffer = canvasProvider.createCanvas(artWidth, artHeight);
            bufferCtx = buffer.getContext('2d');
        }
        // setup a main canvas buffer
        const canvas = canvasProvider.createCanvas(renderWidth, renderHeight);
        let ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, imageWidth, imageHeight);
        ctx.drawImage(image, artX, artY, artWidth, artHeight, 0, 0, artWidth, artHeight);
        const transparencyInset = getEdgeTransparencyInsets(canvas, {
            x: 0,
            y: 0,
            width: artWidth,
            height: artHeight,
        });
        // NOTE: image content bounds exluding any transparent borders
        const imageContentBounds = {
            x: transparencyInset.left,
            y: transparencyInset.top,
            width: artWidth - (transparencyInset.left + transparencyInset.right),
            height: artHeight - (transparencyInset.top + transparencyInset.bottom),
        };
        ctx.clearRect(0, 0, imageWidth, imageHeight);
        if (background) {
            ctx.save();
            switch (background.type) {
                case 'solid':
                    ctx.fillStyle = background.color;
                    ctx.fillRect(0, 0, renderWidth, renderHeight);
                    break;
                case 'gradient':
                    {
                        const gradient = createLinearGradient('grad', {
                            width: renderWidth,
                            height: renderHeight,
                        }, background.rotation, background.colors);
                        const grad = ctx.createLinearGradient(gradient.p0.x, gradient.p0.y, gradient.p1.x, gradient.p1.y);
                        gradient.stops.forEach(step => {
                            grad.addColorStop(step.offset, step.color);
                        });
                        ctx.fillStyle = grad;
                        ctx.fillRect(0, 0, renderWidth, renderHeight);
                    }
                    break;
                default:
                    break;
            }
            ctx.restore();
        }
        ctx.save();
        ctx.translate(padding, padding);
        if (shadow) {
            ctx.save();
            ctx.fillStyle = '#000000';
            ctx.shadowColor = 'black';
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 8;
            ctx.shadowBlur = ((_d = style === null || style === void 0 ? void 0 : style.shadow) !== null && _d !== void 0 ? _d : 0) * 2;
            ctx.beginPath();
            // NOTE: apply some extra antialias
            ctx.roundRect(imageContentBounds.x + 1, imageContentBounds.y + 1, imageContentBounds.width - 2, imageContentBounds.height - 2, cornerRadius);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        }
        ctx.fillStyle = 'transparent';
        ctx.beginPath();
        ctx.roundRect(imageContentBounds.x, imageContentBounds.y, imageContentBounds.width, imageContentBounds.height, cornerRadius);
        ctx.clip();
        ctx.translate(-artX, -artY);
        ctx.drawImage(image, 0, 0, imageWidth, imageHeight);
        // -----------------------
        // 1. Prepare page image
        // -----------------------
        // -----------------------
        // 1.1 Blurs
        // -----------------------
        const hasBlurs = markup.blurs.length > 0;
        if (hasBlurs) {
            const forceSolidBlurs = !blurredImage;
            if (blurredImage) {
                ctx.save();
                ctx.beginPath();
                markup.blurs.forEach(marker => {
                    const rect = getItemAbsoluteBox(marker);
                    ctx.rect(rect.x, rect.y, rect.width, rect.height);
                });
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(blurredImage, 0, 0, imageWidth, imageHeight);
                ctx.restore();
            }
            // solid
            markup.blurs.forEach(marker => {
                if (forceSolidBlurs || marker.method === 'solid') {
                    const rect = getItemAbsoluteBox(marker);
                    ctx.save();
                    ctx.fillStyle = 'black';
                    ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
                    ctx.restore();
                }
            });
        }
        // -----------------------
        // 1.2. Spots
        // -----------------------
        if (hasSpots) {
            bufferCtx.save();
            bufferCtx.clearRect(0, 0, artWidth, artHeight);
            bufferCtx.translate(-artX, -artY);
            renderSpotsWithComposition(markup.spots, bufferCtx, renderArea);
            ctx.drawImage(bufferCtx.canvas, artX, artY);
            bufferCtx.resetTransform();
            bufferCtx.restore();
        }
        ctx.restore();
        ctx.translate(padding, padding);
        ctx.translate(-artX, -artY);
        // -----------------------
        // 2. Create entire page image
        // -----------------------
        // -----------------------
        // 3. Markers
        // -----------------------
        const { marks } = markup;
        if (marks.length) {
            // prepare an extra buffer if there are any zooms
            if (hasZooms) {
                bufferCtx.clearRect(0, 0, artWidth, artHeight);
                bufferCtx.putImageData(ctx.getImageData(padding, padding, artWidth, artHeight), 0, 0);
                // bufferCtx.drawImage(
                //     ctx.canvas as unknown as CanvasImageSource,
                //     padding - artX,
                //     padding - artY,
                //     artWidth,
                //     artHeight,
                //     0,
                //     0,
                //     artWidth,
                //     artHeight
                // );
            }
            marks.forEach(marker => {
                ctx.save();
                const globalTransform = ctx.getTransform();
                switch (marker.alias) {
                    case 'Zoom':
                        {
                            const zoomMarker = marker;
                            renderZoomMaker(zoomMarker, viewToImageScale, bufferCtx.canvas, ctx, renderArea, opts);
                        }
                        break;
                    default:
                        {
                            const renderer = MarkerRenderers[marker.alias];
                            renderer === null || renderer === void 0 ? void 0 : renderer(marker, opts, ctx, images);
                        }
                        break;
                }
                ctx.setTransform(globalTransform);
                ctx.restore();
            });
        }
        // -----------------------
        // 4. Pins
        // -----------------------
        const { pins } = markup;
        if (pins.length) {
            pins.forEach(pin => {
                renderPinMarker(pin, renderJob.fonts, ctx, opts);
            });
        }
        // -----------------------
        // 5. Texts
        // -----------------------
        const { texts } = markup;
        if (texts.length) {
            texts.forEach(text => {
                renderTextMarker(text, renderJob.fonts, ctx, opts);
            });
        }
        // -----------------------
        // 6. Crop to output size
        // -----------------------
        const finalCanvas = canvas;
        // if (applyCropAfterRender) {
        //     const bufferCanvas = bufferCtx.canvas;
        //     const { width, height } = artBox;
        //     bufferCanvas.width = width;
        //     bufferCanvas.height = height;
        //     const lCtx = bufferCanvas.getContext('2d') as CanvasContext;
        //     lCtx.clearRect(0, 0, width, height);
        //     lCtx.drawImage(
        //         canvas as unknown as CanvasImageSource,
        //         artBox.x,
        //         artBox.y,
        //         width,
        //         height,
        //         0,
        //         0,
        //         width,
        //         height
        //     );
        //     finalCanvas = bufferCanvas as Canvas;
        // }
        ctx.resetTransform();
        // -----------------------
        // 7. Watermark
        // -----------------------
        if (watermark) {
            const { placement, offset, size } = watermark;
            let x = 0;
            let y = 0;
            switch (placement) {
                case 'top-left':
                    x = offset.x;
                    y = offset.y;
                    break;
                case 'top-right':
                    x = finalCanvas.width - size.width - offset.x;
                    y = offset.y;
                    break;
                case 'bottom-right':
                    x = finalCanvas.width - size.width - offset.x;
                    y = finalCanvas.height - size.height - offset.y;
                    break;
                case 'center':
                    x = (finalCanvas.width - size.width) * 0.5 + offset.x;
                    y = (finalCanvas.height - size.height) * 0.5 + offset.y;
                    break;
                case 'bottom-left':
                default:
                    x = offset.x;
                    y = finalCanvas.height - size.height - offset.y;
                    break;
            }
            ctx = finalCanvas.getContext('2d');
            ctx.drawImage(watermark.image, x, y, size.width, size.height);
        }
        return finalCanvas;
    });
}
